import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import {
  Brain, TrendingUp, TrendingDown, Minus, Shield, Zap, Heart,
  AlertTriangle, Loader2, Activity, CheckCircle, Moon, Leaf,
  Droplets, Clock, Target, Anchor, ArrowRight, RotateCcw
} from 'lucide-react';

const ICONS = { moon: Moon, leaf: Leaf, droplet: Droplets, heart: Heart, alert: AlertTriangle, brain: Brain, target: Target, shield: Shield, anchor: Anchor };

const PATH_STYLES = {
  current: { color: '#94a3b8', bg: 'bg-slate-500/5', border: 'border-slate-500/10', label: 'Current Path' },
  improved: { color: '#34d399', bg: 'bg-emerald-500/5', border: 'border-emerald-500/10', label: 'Improved Path' },
  worsened: { color: '#f87171', bg: 'bg-rose-500/5', border: 'border-rose-500/10', label: 'Risk Path' },
};

const STATE_COLORS = {
  calm_alignment: '#34d399', performative_flow: '#a78bfa', nervous_system_strain: '#f87171',
  cognitive_overload: '#fbbf24', emotional_fatigue: '#c084fc', recovery_debt: '#94a3b8',
  anxiety_loop: '#fb7185', fragmented_focus: '#fb923c', irritable_reactivity: '#ef4444',
};

export default function SimulationSandbox() {
  const navigate = useNavigate();
  const [presets, setPresets] = useState({});
  const [mode, setMode] = useState('therapeutic');
  const [simId, setSimId] = useState(null);
  const [baseSnapshot, setBaseSnapshot] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/twinflo/simulation/presets`),
      axios.post(`${API}/twinflo/simulation/start`, {}),
    ]).then(([presetsRes, simRes]) => {
      setPresets(presetsRes.data || {});
      setSimId(simRes.data?.simulation_id);
      setBaseSnapshot(simRes.data?.base_state_snapshot);
    }).catch(() => {}).finally(() => setStarting(false));
  }, []);

  const runScenario = async (preset) => {
    if (!simId) return;
    setLoading(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/twinflo/simulation/${simId}/scenario`, {
        action: preset.action,
        intensity: preset.default_intensity,
        duration: preset.default_duration,
      });
      setResult(res.data);
    } catch { /* ignore */ }
    setLoading(false);
  };

  const resetSim = () => {
    setResult(null);
    setStarting(true);
    axios.post(`${API}/twinflo/simulation/start`, {}).then(r => {
      setSimId(r.data?.simulation_id);
      setBaseSnapshot(r.data?.base_state_snapshot);
    }).finally(() => setStarting(false));
  };

  if (starting) return (
    <div className="min-h-screen bg-[#0a0e14] flex items-center justify-center">
      <Loader2 className="w-5 h-5 animate-spin" style={{ color: '#c8b89a' }} />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0a0e14]" data-testid="simulation-sandbox">
      <div className="max-w-2xl mx-auto px-5 py-6 space-y-5">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-['Work_Sans']" style={{ color: '#c8b89a40' }}>TwinFlo Simulation</div>
            <h1 className="text-xl font-['Outfit'] font-bold" style={{ color: '#c8b89a' }}>Explore Your Future</h1>
          </div>
          <Badge className="text-[9px] bg-amber-500/10 text-amber-400 border-0">Simulation Only</Badge>
        </div>

        {/* Base State */}
        {baseSnapshot && (
          <div className="rounded-xl p-3 border" style={{ borderColor: `${STATE_COLORS[baseSnapshot.state] || '#94a3b8'}20`, backgroundColor: `${STATE_COLORS[baseSnapshot.state] || '#94a3b8'}05` }}>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: STATE_COLORS[baseSnapshot.state] || '#94a3b8' }} />
              <span className="text-xs" style={{ color: '#c8b89a80' }}>Current: <strong style={{ color: '#c8b89a' }}>{baseSnapshot.state?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</strong></span>
              <span className="text-[10px]" style={{ color: '#c8b89a30' }}>Reg: {baseSnapshot.scores?.regulation} | Res: {baseSnapshot.scores?.resilience}</span>
            </div>
          </div>
        )}

        {/* Result View */}
        {result ? (
          <ResultView result={result} onReset={resetSim} />
        ) : (
          <>
            {/* Mode Toggle */}
            <div className="flex gap-1">
              {['therapeutic', 'performative'].map(m => (
                <button key={m} onClick={() => setMode(m)}
                  className={`flex-1 py-2 rounded-xl text-[10px] uppercase tracking-[0.1em] font-['Work_Sans'] transition-all ${mode === m ? 'bg-white/[0.06] text-white/70 border border-white/[0.08]' : 'text-white/20 hover:text-white/40 border border-transparent'}`}>
                  {m}
                </button>
              ))}
            </div>

            {/* Preset Grid */}
            <div className="space-y-2" data-testid="preset-grid">
              {(presets[mode] || []).map(preset => {
                const Icon = ICONS[preset.icon] || Activity;
                const isNeg = ['ignore_signals', 'overload_continues'].includes(preset.id);
                return (
                  <button key={preset.id} onClick={() => runScenario(preset)} disabled={loading}
                    className={`w-full text-left rounded-xl p-4 border transition-all hover:border-white/[0.1] ${isNeg ? 'bg-rose-950/10 border-rose-500/10' : 'bg-white/[0.02] border-white/[0.04]'}`}
                    data-testid={`preset-${preset.id}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${isNeg ? 'bg-rose-500/10' : 'bg-white/[0.04]'}`}>
                        <Icon className="w-4 h-4" style={{ color: isNeg ? '#f87171' : '#c8b89a60' }} />
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-['Outfit'] font-medium" style={{ color: isNeg ? '#fca5a5' : '#c8b89a' }}>{preset.label}</div>
                        <div className="text-[10px] mt-0.5" style={{ color: '#c8b89a40' }}>{preset.description}</div>
                      </div>
                      <ArrowRight className="w-4 h-4" style={{ color: '#c8b89a20' }} />
                    </div>
                  </button>
                );
              })}
            </div>

            {loading && (
              <div className="flex justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin" style={{ color: '#c8b89a' }} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// RESULT VIEW
// ═══════════════════════════════════════════════════════════

function ResultView({ result, onReset }) {
  const scenario = result?.scenario || {};
  const comparison = result?.comparison || {};
  const paths = scenario.paths || {};

  return (
    <div className="space-y-4" data-testid="simulation-result">
      {/* Action label */}
      <div className="text-center">
        <div className="text-[10px] uppercase tracking-wider" style={{ color: '#c8b89a40' }}>Simulating</div>
        <div className="text-lg font-['Outfit'] font-semibold" style={{ color: '#c8b89a' }}>{scenario.action_label}</div>
        <div className="flex items-center justify-center gap-2 mt-1">
          <Badge className="text-[8px] bg-white/5 text-white/40 border-0">{scenario.intensity}</Badge>
          <Badge className="text-[8px] bg-white/5 text-white/40 border-0">{scenario.duration}</Badge>
          <Badge className={`text-[8px] border-0 ${scenario.confidence === 'high' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-amber-500/15 text-amber-400'}`}>
            {scenario.confidence === 'high' ? 'personalized' : 'projected'}
          </Badge>
        </div>
      </div>

      {/* 3 Path Cards */}
      <div className="space-y-2">
        {['current', 'improved', 'worsened'].map(pathKey => {
          const path = paths[pathKey];
          if (!path) return null;
          const style = PATH_STYLES[pathKey];
          const stateColor = STATE_COLORS[path.state] || '#94a3b8';
          const isBest = comparison.best_path === pathKey;

          return (
            <Card key={pathKey} className={`${style.bg} border ${style.border} ${isBest ? 'ring-1' : ''}`} style={isBest ? { ringColor: style.color } : {}}>
              <CardContent className="py-3 px-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: style.color }} />
                    <span className="text-xs font-['Outfit'] font-medium" style={{ color: style.color }}>{style.label}</span>
                    {isBest && <Badge className="text-[7px] bg-emerald-500/15 text-emerald-400 border-0">Best outcome</Badge>}
                  </div>
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: stateColor }} title={path.state} />
                </div>
                <p className="text-[11px] mb-2" style={{ color: '#c8b89a60' }}>{path.label}</p>
                {/* Score bars */}
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { key: 'regulation', label: 'Reg', good: 'high' },
                    { key: 'cognitive_strain', label: 'Cog', good: 'low' },
                    { key: 'resilience', label: 'Res', good: 'high' },
                  ].map(s => {
                    const val = path.scores?.[s.key] ?? 50;
                    const delta = path.deltas?.[s.key] ?? 0;
                    const isGood = s.good === 'high' ? delta > 0 : delta < 0;
                    return (
                      <div key={s.key}>
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="text-[8px]" style={{ color: '#c8b89a40' }}>{s.label}</span>
                          <span className="text-[9px] font-semibold" style={{ color: isGood ? '#34d399' : delta !== 0 ? '#f87171' : '#c8b89a40' }}>
                            {val} {delta > 0 ? `+${delta}` : delta < 0 ? delta : ''}
                          </span>
                        </div>
                        <div className="h-1 rounded-full" style={{ backgroundColor: '#ffffff06' }}>
                          <div className="h-full rounded-full" style={{ width: `${val}%`, backgroundColor: style.color }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Key Differences */}
      {scenario.key_differences?.length > 0 && (
        <div className="rounded-xl p-3 border border-white/[0.04]" style={{ backgroundColor: '#ffffff02' }}>
          <div className="text-[9px] uppercase tracking-wider mb-2" style={{ color: '#c8b89a30' }}>Key Differences</div>
          {scenario.key_differences.map((d, i) => (
            <div key={i} className="flex items-center gap-1.5 mb-1 text-[10px]" style={{ color: '#c8b89a60' }}>
              <CheckCircle className="w-3 h-3 flex-shrink-0" style={{ color: '#34d39960' }} />
              {d}
            </div>
          ))}
        </div>
      )}

      {/* Summary */}
      <div className="rounded-xl p-4" style={{ backgroundColor: '#34d39908', borderColor: '#34d39915', border: '1px solid #34d39915' }}>
        <p className="text-xs leading-relaxed" style={{ color: '#c8b89a80' }}>{comparison.summary}</p>
      </div>

      {/* Try Another */}
      <Button onClick={onReset} variant="ghost" className="w-full text-white/30 hover:text-white/60 text-xs" data-testid="try-another-btn">
        <RotateCcw className="w-3 h-3 mr-1.5" /> Try Another Scenario
      </Button>
    </div>
  );
}
